// ============================================================================
// KLINISOL CRO - Electronic Signature Component (REDESIGNED)
// Pure Tailwind CSS - Matches Dashboard/Navbar design system
// FDA 21 CFR Part 11 Compliant E-Signature Modal
// ============================================================================

import React, { useState } from 'react';
import { 
    X, 
    PenLine, 
    Eye, 
    EyeOff, 
    Lock, 
    AlertCircle, 
    Info,
    Shield,
    Loader2
} from 'lucide-react';

const ElectronicSignature = ({ onSubmit, onCancel, moduleName, userName }) => {
    const [signatureText, setSignatureText] = useState(userName || '');
    const [pin, setPin] = useState('');
    const [confirmPin, setConfirmPin] = useState('');
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPin, setShowPin] = useState(false);
    
    // Generate signature meaning
    const signatureMeaning = `I, ${signatureText || '[Your Full Name]'}, certify that I have successfully completed the ${moduleName} training module and understand all the material presented. I confirm that I completed this training myself and that the information provided is accurate and true to the best of my knowledge. This electronic signature is legally binding and constitutes my agreement that this signature has the same legal effect as a handwritten signature.`;
    
    // Validation
    const validate = () => {
        const errors = [];
        
        const trimmedSignature = signatureText.trim();
        if (trimmedSignature.length < 3) {
            errors.push('Please enter your full name (minimum 3 characters)');
        }
        
        if (trimmedSignature.length > 255) {
            errors.push('Name must not exceed 255 characters');
        }
        
        if (!/^[a-zA-Z\s\-']+$/.test(trimmedSignature)) {
            errors.push('Name must contain only letters, spaces, hyphens, and apostrophes');
        }
        
        if (!/^\d{4,6}$/.test(pin)) {
            errors.push('PIN must be 4-6 digits');
        }
        
        if (pin !== confirmPin) {
            errors.push('PINs do not match');
        }
        
        if (!agreedToTerms) {
            errors.push('You must agree to the signature terms');
        }
        
        return errors;
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const validationErrors = validate();
        if (validationErrors.length > 0) {
            setError(validationErrors.join('. '));
            return;
        }
        
        setLoading(true);
        setError('');
        
        try {
            await onSubmit({
                signatureText: signatureText.trim(),
                pin: pin
            });
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to submit signature');
            setLoading(false);
        }
    };
    
    const handlePinChange = (value, isConfirm = false) => {
        const filtered = value.replace(/\D/g, '').slice(0, 6);
        
        if (isConfirm) {
            setConfirmPin(filtered);
        } else {
            setPin(filtered);
        }
    };

    return (
        <div 
            onClick={onCancel}
            className="fixed inset-0 bg-slate-900/75 backdrop-blur-sm flex items-center justify-center z-[1000] p-4 animate-fadeIn"
        >
            <div 
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-auto shadow-2xl animate-slideUp"
            >
                {/* Modal Header */}
                <div className="bg-gradient-to-r from-[#475569] to-[#334155] p-7 rounded-t-3xl flex justify-between items-center text-white">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                            <PenLine className="w-6 h-6 text-white" />
                        </div>
                        <h2 className="text-2xl font-black">
                            Electronic Signature Required
                        </h2>
                    </div>
                    <button 
                        onClick={onCancel}
                        className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg text-white hover:bg-white/30 transition-all flex items-center justify-center"
                        aria-label="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                
                <div className="p-8">
                    {/* Info Box */}
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-[#475569] p-5 rounded-xl mb-8">
                        <div className="flex gap-3 items-start">
                            <div className="w-6 h-6 bg-[#475569] rounded-full flex items-center justify-center text-white flex-shrink-0">
                                <Info className="w-4 h-4" />
                            </div>
                            <p className="text-sm text-[#475569] leading-relaxed">
                                <strong>FDA 21 CFR Part 11 Compliance:</strong> Your electronic signature 
                                legally certifies completion of this training with the same authority as a 
                                handwritten signature.
                            </p>
                        </div>
                    </div>
                    
                    <form onSubmit={handleSubmit}>
                        {/* Signature Text Input */}
                        <div className="mb-6">
                            <label 
                                htmlFor="signatureText"
                                className="block mb-2 text-sm font-bold text-gray-900 uppercase tracking-wide"
                            >
                                Type Your Full Legal Name *
                            </label>
                            <input
                                id="signatureText"
                                type="text"
                                value={signatureText}
                                onChange={(e) => setSignatureText(e.target.value)}
                                placeholder="John Doe"
                                maxLength={255}
                                autoComplete="off"
                                disabled={loading}
                                className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl text-base transition-all bg-gray-50 font-medium focus:outline-none focus:border-[#475569] focus:bg-white focus:ring-4 focus:ring-[#475569]/10 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                            <p className="mt-2 text-xs text-gray-600">
                                This will appear on your certificate and official records
                            </p>
                        </div>
                        
                        {/* PIN Input */}
                        <div className="mb-6">
                            <label 
                                htmlFor="pin"
                                className="block mb-2 text-sm font-bold text-gray-900 uppercase tracking-wide"
                            >
                                Create Your PIN (4-6 digits) *
                            </label>
                            <div className="relative">
                                <input
                                    id="pin"
                                    type={showPin ? "text" : "password"}
                                    value={pin}
                                    onChange={(e) => handlePinChange(e.target.value)}
                                    placeholder="••••"
                                    inputMode="numeric"
                                    maxLength={6}
                                    autoComplete="off"
                                    disabled={loading}
                                    className="w-full px-4 py-3.5 pr-12 border-2 border-gray-200 rounded-xl text-base transition-all bg-gray-50 font-semibold tracking-wider focus:outline-none focus:border-[#475569] focus:bg-white focus:ring-4 focus:ring-[#475569]/10 disabled:opacity-50 disabled:cursor-not-allowed"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPin(!showPin)}
                                    tabIndex={-1}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-900 transition-colors p-1"
                                >
                                    {showPin ? (
                                        <Eye className="w-5 h-5" />
                                    ) : (
                                        <EyeOff className="w-5 h-5" />
                                    )}
                                </button>
                            </div>
                        </div>
                        
                        {/* Confirm PIN */}
                        <div className="mb-6">
                            <label 
                                htmlFor="confirmPin"
                                className="block mb-2 text-sm font-bold text-gray-900 uppercase tracking-wide"
                            >
                                Confirm PIN *
                            </label>
                            <input
                                id="confirmPin"
                                type={showPin ? "text" : "password"}
                                value={confirmPin}
                                onChange={(e) => handlePinChange(e.target.value, true)}
                                placeholder="••••"
                                inputMode="numeric"
                                maxLength={6}
                                autoComplete="off"
                                disabled={loading}
                                className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl text-base transition-all bg-gray-50 font-semibold tracking-wider focus:outline-none focus:border-[#475569] focus:bg-white focus:ring-4 focus:ring-[#475569]/10 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                        </div>
                        
                        {/* Signature Meaning */}
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-200 p-5 rounded-xl mb-6">
                            <h4 className="mb-3 text-sm font-bold text-gray-900 uppercase tracking-wide">
                                Signature Meaning:
                            </h4>
                            <div className="text-sm text-gray-700 leading-relaxed italic">
                                {signatureMeaning}
                            </div>
                        </div>
                        
                        {/* Terms Agreement */}
                        <div className="mb-6">
                            <label className="flex items-start gap-3 cursor-pointer p-4 bg-gray-50 rounded-xl border-2 border-gray-200 transition-all hover:border-[#475569] hover:bg-white">
                                <input
                                    type="checkbox"
                                    checked={agreedToTerms}
                                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                                    disabled={loading}
                                    className="w-5 h-5 cursor-pointer accent-[#475569] mt-0.5"
                                />
                                <span className="text-sm text-gray-900 leading-relaxed flex-1">
                                    I have read and agree to the signature meaning above. I understand 
                                    that this electronic signature is legally binding. *
                                </span>
                            </label>
                        </div>
                        
                        {/* Error Message */}
                        {error && (
                            <div className="bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-500 p-4 rounded-xl mb-6 flex gap-3 items-start">
                                <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white flex-shrink-0">
                                    <AlertCircle className="w-4 h-4" />
                                </div>
                                <span className="text-sm text-red-900 leading-relaxed">
                                    {error}
                                </span>
                            </div>
                        )}
                        
                        {/* Modal Actions */}
                        <div className="flex gap-4 flex-wrap">
                            <button 
                                type="button"
                                onClick={onCancel}
                                disabled={loading}
                                className="flex-1 min-w-[150px] px-6 py-4 bg-gray-100 text-gray-700 rounded-xl text-base font-semibold cursor-pointer transition-all hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit"
                                disabled={loading || !agreedToTerms}
                                className={`flex-[2] min-w-[200px] px-6 py-4 rounded-xl text-base font-bold transition-all flex items-center justify-center gap-2 ${
                                    (loading || !agreedToTerms)
                                        ? 'bg-gray-400 text-white cursor-not-allowed opacity-60'
                                        : 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:-translate-y-0.5 shadow-lg hover:shadow-xl'
                                }`}
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        <span>Submitting...</span>
                                    </>
                                ) : (
                                    <>
                                        <PenLine className="w-5 h-5" />
                                        <span>Sign & Complete Training</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                    
                    {/* Security Note */}
                    <div className="mt-8 p-5 bg-gradient-to-br from-cyan-50 to-blue-50 border-2 border-cyan-200 rounded-xl">
                        <div className="flex gap-3 items-start">
                            <Shield className="w-6 h-6 text-cyan-600 flex-shrink-0" />
                            <p className="text-xs text-cyan-900 leading-relaxed">
                                <strong>Security:</strong> Your signature is encrypted and stored 
                                securely. Your PIN is hashed and cannot be recovered. This signature 
                                includes your IP address and timestamp for audit compliance.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(50px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </div>
    );
};

export default ElectronicSignature;